import React, { useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring } from "framer-motion";
import {
    ArrowRight,
    BookOpenCheck,
    CircleDollarSign,
    Fingerprint,
    ScanLine,
    ShieldCheck,
    Sparkles,
    TicketCheck,
    WalletCards
} from "lucide-react";
import EthosinoTypewriter from "../../../../Helpers/Typewriter/EthosinoTypewriter";
import "./SweepstakesHero3D.css";

/**
 * A self-contained replacement for the existing .siv6-hero__copy block.
 * Uses perspective transforms and Framer Motion, not WebGL or Canvas.
 * Pointer effects are disabled for touch and reduced-motion users.
 */
const SweepstakesHeroCopy3D = ({ reduceMotion = false }) => {
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const tiltX = useSpring(y, { stiffness: 100, damping: 24, mass: 0.75 });
    const tiltY = useSpring(x, { stiffness: 100, damping: 24, mass: 0.75 });

    const handlePointerMove = useCallback((event) => {
        if (reduceMotion || event.pointerType === "touch") return;
        const rect = event.currentTarget.getBoundingClientRect();
        if (!rect.width || !rect.height) return;

        const horizontal = (event.clientX - rect.left) / rect.width - 0.5;
        const vertical = (event.clientY - rect.top) / rect.height - 0.5;

        x.set(horizontal * 6);
        y.set(vertical * -5);
        event.currentTarget.style.setProperty("--siv6-hero-pointer-x", `${(horizontal + 0.5) * 100}%`);
        event.currentTarget.style.setProperty("--siv6-hero-pointer-y", `${(vertical + 0.5) * 100}%`);
    }, [reduceMotion, x, y]);

    const resetPointer = useCallback((event) => {
        x.set(0);
        y.set(0);
        event.currentTarget.style.setProperty("--siv6-hero-pointer-x", "50%");
        event.currentTarget.style.setProperty("--siv6-hero-pointer-y", "30%");
    }, [x, y]);

    return (
        <motion.div
            className="siv6-hero__copy siv6-hero3d"
            style={reduceMotion ? undefined : { rotateX: tiltX, rotateY: tiltY }}
            onPointerMove={handlePointerMove}
            onPointerLeave={resetPointer}
        >
            {/* Background exists only for visual depth; it does not capture interaction. */}
            <div className="siv6-hero3d__atmosphere" aria-hidden="true">
                <span className="siv6-hero3d__mesh" />
                <span className="siv6-hero3d__light" />
                <span className="siv6-hero3d__orbit siv6-hero3d__orbit--outer" />
                <span className="siv6-hero3d__orbit siv6-hero3d__orbit--inner" />
                <span className="siv6-hero3d__corner siv6-hero3d__corner--tl" />
                <span className="siv6-hero3d__corner siv6-hero3d__corner--br" />
            </div>

            <motion.div
                className="siv6-system-badge siv6-hero3d__badge"
                initial={reduceMotion ? false : { opacity: 0, y: -18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            >
                <span className="siv6-system-badge__pulse" />
                <span>DUAL-CURRENCY SWEEPSTAKES DEPLOYMENT</span>
                <span className="siv6-system-badge__divider" />
                <span>PROGRAM RULES + PLAYER EXPERIENCE</span>
            </motion.div>

            <motion.h1
                className="siv6-hero3d__headline"
                initial={reduceMotion ? false : { opacity: 0, y: 42, rotateX: -9 }}
                animate={{ opacity: 1, y: 0, rotateX: 0 }}
                transition={{ duration: 0.92, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
            >
                <span className="siv6-hero3d__headline-first">Two balances.</span>
                <span className="siv6-hero3d__headline-gradient">One clear promotion.</span>
            </motion.h1>

            {/* Keep the typewriter OUTSIDE the large h1: its measure/live grid can
          otherwise reserve excessive height and cause the giant gap. */}
            <motion.div
                className="siv6-hero3d__typewriter-panel"
                initial={reduceMotion ? false : { opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.66, delay: 0.21 }}
            >
                <div className="siv6-hero3d__typewriter-label">
                    <span className="siv6-hero3d__signal" />
                    <span>BUILT FOR PROMOTIONAL OPERATIONS</span>
                    <Sparkles size={14} aria-hidden="true" />
                </div>
                <div className="siv6-hero3d__typewriter-text">
                    <EthosinoTypewriter
                        phrases={[
                            "Separate play credits from promotional rewards.",
                            "Support free entry without requiring a purchase.",
                            "Track promotional awards from entry to redemption.",
                            "Configure the rules behind every promotion.",
                            "Build a sweepstakes experience around your brand."
                        ]}
                        typingSpeed={55}
                        eraseSpeed={28}
                        holdDelay={2300}
                        betweenDelay={350}
                    />
                </div>
            </motion.div>

            <motion.p
                className="siv6-hero__lead siv6-hero3d__lead"
                initial={reduceMotion ? false : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.72, delay: 0.29 }}
            >
                A sweepstakes-style casino needs more than casino games with renamed balances.
                Ethosino supports an operator-defined promotional experience with separate
                entertainment and promotional credits, an available no-purchase entry path,
                prize-claim review and the records needed to explain each award. Exactly
                how these elements are offered follows the operator&apos;s approved rules
                and provider setup.
            </motion.p>

            <motion.div
                className="siv6-hero3d__currency-pair"
                initial={reduceMotion ? false : { opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.68, delay: 0.35 }}
                aria-label="Illustrative dual-currency distinction"
            >
                <motion.div className="siv6-hero3d__coin-card siv6-hero3d__coin-card--gold"
                    whileHover={reduceMotion ? {} : { y: -5, rotateY: -3 }}
                    transition={{ type: "spring", stiffness: 240, damping: 22 }}
                >
                    <span className="siv6-hero3d__coin-symbol" aria-hidden="true">GC</span>
                    <div><small>ENTERTAINMENT</small><strong>Gold Coins</strong><span>Play-only credits</span></div>
                    <CircleDollarSign size={17} aria-hidden="true" />
                </motion.div>
                <motion.div className="siv6-hero3d__coin-card siv6-hero3d__coin-card--sweeps"
                    whileHover={reduceMotion ? {} : { y: -5, rotateY: 3 }}
                    transition={{ type: "spring", stiffness: 240, damping: 22 }}
                >
                    <span className="siv6-hero3d__coin-symbol" aria-hidden="true">SC</span>
                    <div><small>PROMOTIONAL</small><strong>Sweeps Coins</strong><span>Rules-governed credits</span></div>
                    <TicketCheck size={17} aria-hidden="true" />
                </motion.div>
            </motion.div>

            <motion.div
                className="siv6-hero__thesis siv6-hero3d__thesis"
                initial={reduceMotion ? false : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.43 }}
            >
                <ShieldCheck size={20} aria-hidden="true" />
                <p>
                    <strong>Player expectations should match the official rules.</strong>{" "}
                    If a balance cannot be redeemed, say so clearly. If an entry is free,
                    make that route understandable. If a prize needs review, show its actual status.
                </p>
            </motion.div>

            <motion.div
                className="siv6-hero__actions siv6-hero3d__actions"
                initial={reduceMotion ? false : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.5 }}
            >
                <Link className="siv6-button siv6-button--primary siv6-hero3d__primary" to="/book-demo">
                    <span>Review a sweepstakes deployment</span>
                    <ArrowRight size={17} aria-hidden="true" />
                </Link>
                <a className="siv6-button siv6-button--secondary siv6-hero3d__secondary" href="#evidence-system">
                    <ScanLine size={16} aria-hidden="true" />
                    <span>Explore program components</span>
                </a>
            </motion.div>

            <motion.div
                className="siv6-hero__proofline siv6-hero3d__proofline"
                initial={reduceMotion ? false : { opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.56 }}
            >
                <span><TicketCheck size={15} aria-hidden="true" /> no-purchase entry workflow</span>
                <span><WalletCards size={15} aria-hidden="true" /> distinct coin balances</span>
                <span><BookOpenCheck size={15} aria-hidden="true" /> promotion conditions</span>
                <span><Fingerprint size={15} aria-hidden="true" /> prize request review</span>
            </motion.div>
        </motion.div>
    );
};

export default SweepstakesHeroCopy3D;
